import { GpoCommissionType, useOverrideGpoOrderCommissionApi } from 'apis/gpo-orders';
import {
  Box,
  Button,
  ChakraSelect,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
} from 'design-system';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import React, { useEffect, useMemo, useState } from 'react';

/** The minimum an order row needs to carry to edit its commission — `GpoOrderRow` (GPO Orders
 * page) and the reconciliation orders-modal row both satisfy it structurally. */
export interface ICommissionEditableRow {
  orderId: number;
  orderCode: string;
  orderTotal: number;
  commissionType: GpoCommissionType | null;
  commissionRate: number | null;
  commissionAmount: number | null;
}

export interface IOverrideCommissionModalCell {
  row: ICommissionEditableRow;
  trigger: JSX.Element;
  /** Called after a successful save — for hosts outside the GPO Orders page (the reconciliation
   * orders modal) to invalidate their own queries. */
  onSaved?: () => void;
}

const TYPE_OPTIONS: { value: GpoCommissionType; label: string }[] = [
  { value: 'percent', label: 'Percent (%)' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'waived', label: 'Waived (no commission)' },
];

export const OverrideCommissionModal = ({ row, trigger, onSaved }: IOverrideCommissionModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const overrideCommission = useOverrideGpoOrderCommissionApi();

  const [type, setType] = useState<GpoCommissionType>('percent');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    // Start from the stored terms: fixed rows edit the amount, waived rows stay waived,
    // everything else edits the percent.
    if (row.commissionType === 'fixed') {
      setType('fixed');
      setValue(row.commissionAmount != null ? String(row.commissionAmount) : '');
    } else if (row.commissionType === 'waived') {
      setType('waived');
      setValue('');
    } else {
      setType('percent');
      setValue(row.commissionRate != null ? String(row.commissionRate) : '');
    }
    setReason('');
  }, [isOpen, row]);

  const isWaive = type === 'waived';
  const numeric = value === '' ? null : Number(value);
  const valueError =
    isWaive || numeric == null
      ? null
      : Number.isNaN(numeric)
      ? 'Enter a valid number'
      : !/^\d*(\.\d{0,2})?$/.test(value)
      ? 'Use at most 2 decimal places'
      : type === 'percent' && numeric > 100
      ? 'Percent commission must be between 0 and 100'
      : type === 'fixed' && numeric <= 0
      ? 'Fixed commission must be greater than 0'
      : null;

  const preview = useMemo(() => {
    if (isWaive) return 'No commission will be billed for this order; an unpaid invoice is voided.';
    if (numeric == null || valueError || row.orderTotal <= 0) return null;
    if (type === 'percent') return `= ${((numeric * row.orderTotal) / 100).toFixed(2)} on this order`;
    return `= ${((numeric / row.orderTotal) * 100).toFixed(2)}% of ${row.orderTotal.toFixed(2)}`;
  }, [isWaive, numeric, valueError, type, row.orderTotal]);

  const canSubmit = (isWaive || numeric != null) && !valueError && reason.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await overrideCommission.mutateAsync({
        orderId: row.orderId,
        commissionType: type,
        ...(isWaive ? {} : { commissionValue: numeric as number }),
        reason: reason.trim(),
      });
      onSaved?.();
      onClose();
    } catch {
      // error toast handled by the mutation hook
    }
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'md' }} scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Commission — Order {row.orderCode}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={!!valueError} mb='4'>
              <FormLabel fontSize='sm'>Commission</FormLabel>
              <Flex gap='2' direction={{ base: 'column', sm: 'row' }}>
                <Box width={{ base: 'full', sm: '210px' }} flexShrink={0}>
                  <ChakraSelect
                    options={TYPE_OPTIONS}
                    value={TYPE_OPTIONS.find((o) => o.value === type)}
                    onChange={(opt: any) => setType(opt?.value ?? 'percent')}
                    isSearchable={false}
                  />
                </Box>
                {!isWaive && (
                  <Input
                    width={{ base: 'full', sm: '140px' }}
                    value={value}
                    onChange={(e) => setValue(numOnly(e.target.value))}
                  />
                )}
              </Flex>
              {valueError && (
                <Text fontSize='xs' color='red.500' mt='1'>
                  {valueError}
                </Text>
              )}
              {!valueError && preview && (
                <Text fontSize='xs' color='gray.600' mt='1'>
                  {preview}
                </Text>
              )}
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize='sm'>Reason</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder={
                  isWaive
                    ? 'Why is the commission waived for this order?'
                    : 'Why is this order deviating from the stamped terms?'
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={onClose} isDisabled={overrideCommission.isLoading}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={submit} isDisabled={!canSubmit} isLoading={overrideCommission.isLoading}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
